import React, { useEffect, useState } from "react";
import { Trash2, Mail, Smartphone, Check, X, Pencil } from "lucide-react";
import { api } from "../../lib/api";

const LIMIT_OPTIONS = [
  { value: "1", label: "1 device" },
  { value: "2", label: "2 devices" },
  { value: "3", label: "3 devices" },
  { value: "5", label: "5 devices" },
  { value: "unlimited", label: "Unlimited" },
];

function limitLabel(v) {
  if (v == null) return "Unlimited";
  return `${v} device${v === 1 ? "" : "s"}`;
}

export default function EmailsTab() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "student", limit: "unlimited" });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // email being edited
  const [editLimit, setEditLimit] = useState("unlimited");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/emails");
      setList(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email.trim()) return;
    try {
      await api.post("/admin/emails", {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
        device_limit: form.limit === "unlimited" ? null : Number(form.limit),
      });
      setForm({ email: "", name: "", role: "student", limit: "unlimited" });
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.message || "Couldn't add"));
    }
  };

  const startEdit = (e) => {
    setEditing(e.email);
    setEditLimit(e.device_limit == null ? "unlimited" : String(e.device_limit));
  };
  const saveEdit = async (e) => {
    try {
      await api.patch(`/admin/emails/${encodeURIComponent(e.email)}`,
        editLimit === "unlimited"
          ? { clear_device_limit: true }
          : { device_limit: Number(editLimit) },
      );
      setEditing(null);
      await load();
    } catch (err) {
      console.warn("Update device_limit failed:", err?.message);
    }
  };

  const remove = async (em) => {
    if (!window.confirm(`Remove ${em} from the allowlist?`)) return;
    await api.delete(`/admin/emails/${encodeURIComponent(em)}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="emails-tab">
      <form onSubmit={add} className="bg-white border border-border rounded-2xl p-5 sm:p-6 space-y-4" data-testid="add-email-form">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-sage" strokeWidth={1.7} />
            <div className="font-display text-lg font-semibold">Add allowed email</div>
          </div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Add any email here and the person signs in with Google — no code, no extra step. Pick <b>student</b> or <b>teacher</b>, then optionally restrict how many devices the same account may use.
          </p>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="name@example.com"
            data-testid="email-input"
            className="px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage md:col-span-2"
            required
          />
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Name (optional)"
            data-testid="email-name"
            className="px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            data-testid="email-role"
            className="px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage"
          >
            <option value="student">Student</option>
            <option value="teacher">Teacher</option>
          </select>
        </div>
        <div className="grid md:grid-cols-[1fr_auto] gap-3">
          <select
            value={form.limit}
            onChange={(e) => setForm({ ...form, limit: e.target.value })}
            data-testid="email-device-limit"
            className="px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage"
          >
            {LIMIT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>Device limit: {o.label}</option>
            ))}
          </select>
          <button data-testid="add-email-submit" className="bg-sage hover:bg-sage-dark text-white font-medium px-6 py-2.5 rounded-xl transition">Add</button>
        </div>
        {error && <div data-testid="email-error" className="text-sm text-terracotta">{error}</div>}
      </form>

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-border rounded-2xl overflow-hidden" data-testid="emails-list">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-4 py-3">Email</th>
              <th className="text-start px-4 py-3">Role</th>
              <th className="text-start px-4 py-3">Device limit</th>
              <th className="text-start px-4 py-3">Bound</th>
              <th className="text-start px-4 py-3">Added</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && list.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No emails yet</td></tr>}
            {!loading && list.map((e) => (
              <tr key={e.email} className="border-t border-border" data-testid={`email-row-${e.email}`}>
                <td className="px-4 py-3 font-medium">{e.email}{e.name ? <span className="ms-1 text-muted-foreground font-normal">· {e.name}</span> : null}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${e.role === "teacher" ? "bg-terracotta/10 text-terracotta" : "bg-sage/10 text-sage"}`}>
                    {e.role || "student"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {editing === e.email ? (
                    <div className="inline-flex items-center gap-1.5">
                      <select value={editLimit} onChange={(ev) => setEditLimit(ev.target.value)} data-testid={`edit-limit-select-${e.email}`} className="px-2 py-1 rounded-md border border-border bg-bone text-xs focus:border-sage focus:outline-none">
                        {LIMIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button onClick={() => saveEdit(e)} data-testid={`save-limit-${e.email}`} className="p-1.5 rounded-md bg-sage/10 text-sage hover:bg-sage/20"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setEditing(null)} className="p-1.5 rounded-md bg-secondary text-muted-foreground hover:bg-secondary/70"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <button onClick={() => startEdit(e)} data-testid={`edit-limit-${e.email}`} className="inline-flex items-center gap-1.5 text-sm hover:text-sage">
                      <Smartphone className="w-3.5 h-3.5" />
                      {limitLabel(e.device_limit)}
                      <Pencil className="w-3 h-3 opacity-50" />
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {e.devices_bound ?? 0}{e.device_limit ? ` / ${e.device_limit}` : ""}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{e.added_at ? new Date(e.added_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-end">
                  <button onClick={() => remove(e.email)} className="p-1.5 rounded-lg hover:bg-terracotta/10 text-terracotta" data-testid={`delete-email-${e.email}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2.5" data-testid="emails-list-mobile">
        {!loading && list.length === 0 && <div className="text-center text-sm text-muted-foreground py-6 bg-white border border-border rounded-2xl">No emails yet</div>}
        {!loading && list.map((e) => (
          <div key={e.email} className="bg-white border border-border rounded-2xl p-4" data-testid={`email-card-${e.email}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{e.email}</div>
                {e.name && <div className="text-xs text-muted-foreground truncate">{e.name}</div>}
                <div className="mt-1 flex items-center gap-1.5">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold ${e.role === "teacher" ? "bg-terracotta/10 text-terracotta" : "bg-sage/10 text-sage"}`}>
                    {e.role || "student"}
                  </span>
                </div>
              </div>
              <button onClick={() => remove(e.email)} className="p-2 rounded-lg hover:bg-terracotta/10 text-terracotta shrink-0"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="text-[11px] text-muted-foreground">Bound: {e.devices_bound ?? 0}{e.device_limit ? ` / ${e.device_limit}` : ""}</div>
              {editing === e.email ? (
                <div className="inline-flex items-center gap-1.5">
                  <select value={editLimit} onChange={(ev) => setEditLimit(ev.target.value)} className="px-2 py-1 rounded-md border border-border bg-bone text-xs focus:border-sage focus:outline-none">
                    {LIMIT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <button onClick={() => saveEdit(e)} className="p-1.5 rounded-md bg-sage/10 text-sage"><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setEditing(null)} className="p-1.5 rounded-md bg-secondary text-muted-foreground"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <button onClick={() => startEdit(e)} className="inline-flex items-center gap-1.5 text-xs hover:text-sage px-2 py-1 rounded-md border border-border">
                  <Smartphone className="w-3.5 h-3.5" />
                  {limitLabel(e.device_limit)}
                  <Pencil className="w-3 h-3 opacity-50" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
